import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createChart } from 'lightweight-charts';
import { X, Pin, ExternalLink, ChevronUp, ChevronDown, TrendingUp, Loader2, AlertCircle } from 'lucide-react';
import apiClient from '../../utils/apiClient';

// ─── Yahoo Finance symbol resolver ───────────────────────────────────────────
// NSE stocks → SYMBOL.NS  |  Indices → ^NSEI  |  Commodities → GC=F etc.
// null = Yahoo Finance has no chart data for this index (show graceful message)
const YF_SYMBOL_MAP = {
  // ── Nifty Broad Indices ──
  'NIFTY 50': '^NSEI', 'NIFTY50': '^NSEI', 'NIFTY': '^NSEI',
  'NIFTY 100': '^CNX100', 'NIFTY100': '^CNX100',
  'NIFTY 200': '^CNX200', 'NIFTY200': '^CNX200',
  'NIFTY 500': '^CNX500', 'NIFTY500': '^CNX500',
  'NIFTY NEXT 50': '^CNXNIFTYC', 'NIFTYNEXT50': '^CNXNIFTYC',  // confirmed: ^CNXNIFTYC not ^NSMIDCP
  'NIFTY LARGEMIDCAP 250': 'NIFTY_LARGEMID_250.NS', 'NIFTYLARGEMIDCAP250': 'NIFTY_LARGEMID_250.NS',
  'NIFTY TOTAL MARKET': 'NIFTY_TOTAL_MKT.NS', 'NIFTYTOTALMARKET': 'NIFTY_TOTAL_MKT.NS',

  // ── Bank / Financial ──
  'BANKNIFTY': '^NSEBANK', 'BANK NIFTY': '^NSEBANK', 'NIFTY BANK': '^NSEBANK', 'NIFTYBANK': '^NSEBANK',
  'FINNIFTY': '^CNXFIN', 'NIFTYFIN': '^CNXFIN',                // confirmed: ^CNXFIN not NIFTY_FIN_SERVICE.NS
  'NIFTY FINANCIAL SERVICES': '^CNXFIN', 'NIFTYFINANCIALSERVICES': '^CNXFIN',
  'NIFTY FINANCIAL SERVICES 25/50': '^CNXFIN',

  // ── Midcap ──
  'NIFTY MIDCAP 100': '^NSEMDCP100', 'NIFTYMIDCAP100': '^NSEMDCP100', 'NIFTY MIDCAP': '^NSEMDCP100', // confirmed: ^CNXMIDCAP
  'NIFTY MIDCAP 50': '^NSEMDCP50', 'NIFTYMIDCAP50': 'NSEMDCP50',       // Yahoo Finance does not carry this index
  'NIFTY MIDCAP 150': '^NSEMDCP150', 'NIFTYMIDCAP150': 'NSEMDCP150',     // Yahoo Finance does not carry this index
  'NIFTY MIDCAP SELECT': 'NIFTY_MID_SELECT.NS', 'NIFTYMIDCAPSELECT': 'NIFTY_MID_SELECT.NS',
  'NIFTY MID SELECT': 'NIFTY_MID_SELECT.NS',
  'NIFTY MIDCAP LIQUID 15': 'NIFTYMIDLIQ15.NS', 'NIFTYMIDLIQ15': 'NIFTYMIDLIQ15.NS',
  'NIFTY MID LIQ 15': 'NIFTYMIDLIQ15.NS',

  // ── Smallcap ──
  'NIFTY SMALLCAP 100': '^NSESMCP100.NS', 'NIFTYSMALLCAP100': '^NSESMCP100.NS',  // confirmed: ^CNXSMALLCAP not ^CNXSC
  'NIFTY SMALLCAP 50': 'NIFTYSMLCAP50.NS', 'NIFTYSMALLCAP50': '^NSESMCP50.NS',
  'NIFTY SMALLCAP 250': '^NSESMCP250.NS', 'NIFTYSMALLCAP250': '^NSESMCP250.NS',

  // ── MidSmallcap ──
  'NIFTY MIDSMALLCAP 400': 'NIFTY_MIDSML_400.NS', 'NIFTYMIDSMALLCAP400': 'NIFTY_MIDSML_400.NS',
  'NIFTY MID SMALL CAP 400': 'NIFTY_MIDSML_400.NS',

  // ── Sector / Thematic ──
  'NIFTY IT': '^CNXIT', 'NIFTYIT': '^CNXIT',
  'NIFTY FMCG': '^CNXFMCG', 'NIFTYFMCG': '^CNXFMCG',
  'NIFTY AUTO': '^CNXAUTO', 'NIFTYAUTO': '^CNXAUTO',
  'NIFTY PHARMA': '^CNXPHARMA', 'NIFTYPHARMA': '^CNXPHARMA',
  'NIFTY METAL': '^CNXMETAL', 'NIFTYMETAL': '^CNXMETAL',
  'NIFTY ENERGY': '^CNXENERGY', 'NIFTYENERGY': '^CNXENERGY',
  'NIFTY INFRA': '^CNXINFRA', 'NIFTYINFRA': '^CNXINFRA',
  'NIFTY INFRASTRUCTURE': '^CNXINFRA', 'NIFTYINFRASTRUCTURE': '^CNXINFRA',
  'NIFTY REALTY': '^CNXREALTY', 'NIFTYREALTY': '^CNXREALTY',
  'NIFTY MEDIA': '^CNXMEDIA', 'NIFTYMEDIA': '^CNXMEDIA',
  'NIFTY PSU BANK': '^CNXPSUBANK', 'NIFTYPSUBANK': '^CNXPSUBANK',
  'NIFTY PRIVATE BANK': '^NIFTYPVTBANK', 'NIFTYPRIVATEBANK': '^NIFTYPVTBANK',
  'NIFTY HEALTHCARE': 'NIFTY_HEALTH.NS', 'NIFTYHEALTHCARE': 'NIFTY_HEALTH.NS',
  'NIFTY CONSUMER DURABLES': 'NIFTY_CONSR_DURBL.NS', 'NIFTYCONSUMERDURABLES': 'NIFTY_CONSR_DURBL.NS',
  'NIFTY OIL AND GAS': 'NIFTY_OIL_GAS.NS', 'NIFTYOILANDGAS': 'NIFTY_OIL_GAS.NS',
  'NIFTY COMMODITIES': '^CNXCOMMO', 'NIFTYCOMMODITIES': '^CNXCOMMO',
  'SENSEX': '^BSESN',


  // ── Global Indices ──
  'S&P 500': '^GSPC', 'SP500': '^GSPC',
  'NASDAQ': '^IXIC', 'NASDAQ 100': '^NDX', 'NASDAQ100': '^NDX',
  'DOW JONES': '^DJI', 'DJIA': '^DJI',
  'S&P SMALLCAP 600': '^SP600G', 'SP600': '^SP600G', 'S&P 600': '^SP600G',
  'FTSE 100': '^FTSE', 'DAX': '^GDAXI',
  'NIKKEI': '^N225', 'NIKKEI 225': '^N225', 'NIKKEI225': '^N225',
  'HANG SENG': '^HSI', 'HANGSENG': '^HSI',
  'SHANGHAI': '000001.SS',
  'CAC 40': '^FCHI', 'ASX 200': '^AXJO', 'KOSPI': '^KS11',

  // ── Commodities (futures) ──
  'GOLD': 'GC=F', 'SILVER': 'SI=F',
  'CRUDE OIL': 'CL=F', 'CRUDE': 'CL=F',
  'NATURAL GAS': 'NG=F', 'NATGAS': 'NG=F',
  'COPPER': 'HG=F', 'ALUMINIUM': 'ALI=F',
  'ZINC': 'ZNC=F', 'NICKEL': 'NI=F',
};

// Returns the Yahoo Finance ticker, null if the symbol is known-unsupported,
// or a best-guess SYMBOL.NS for generic NSE stocks.
const resolveYFSymbol = (name) => {
  if (!name) return null;
  const upper = name.toUpperCase().trim();
  // Explicit mapping (including null for known-unavailable)
  if (Object.prototype.hasOwnProperty.call(YF_SYMBOL_MAP, upper)) {
    return YF_SYMBOL_MAP[upper]; // may be null
  }
  // Default: treat as NSE stock
  return `${name.replace(/[^A-Z0-9&]/gi, '').toUpperCase()}.NS`;
};

// ─── Fetch OHLCV from our backend DB (rsc_index_prices table) ─────────────────
const fetchOHLCFromDB = async (indexName, range = '1y') => {
  // apiClient auto-attaches the JWT Authorization header
  const res = await apiClient.get('/rsc/index-ohlcv', {
    params: { index_name: indexName, range },
    validateStatus: (s) => s < 500, // don't throw on 404
  });
  if (res.status === 404) throw new Error('DB_NOT_FOUND');
  if (res.status !== 200) throw new Error(`DB HTTP ${res.status}`);
  const rows = res.data;
  if (!Array.isArray(rows) || rows.length === 0) throw new Error('DB_NOT_FOUND');

  // Convert YYYY-MM-DD strings to unix seconds (lightweight-charts needs integer seconds)
  return rows
    .map(d => ({
      time: Math.floor(new Date(d.time).getTime() / 1000),
      open:  d.open  ?? d.close,
      high:  d.high  ?? d.close,
      low:   d.low   ?? d.close,
      close: d.close,
    }))
    .filter(d => d.close != null);
};

// ─── Fetch OHLCV for STOCKS from our backend DB (rsc_stock_prices table) ──────
const fetchStockOHLCFromDB = async (symbol, range = '1y') => {
  const res = await apiClient.get('/rsc/stock-ohlcv', {
    params: { symbol, range },
    validateStatus: (s) => s < 500,
  });
  if (res.status === 404) throw new Error('DB_NOT_FOUND');
  if (res.status !== 200) throw new Error(`DB HTTP ${res.status}`);
  const rows = res.data;
  if (!Array.isArray(rows) || rows.length === 0) throw new Error('DB_NOT_FOUND');

  return rows
    .map(d => ({
      time: Math.floor(new Date(d.time).getTime() / 1000),
      open:  d.open  ?? d.close,
      high:  d.high  ?? d.close,
      low:   d.low   ?? d.close,
      close: d.close,
    }))
    .filter(d => d.close != null);
};

// ─── Fetch OHLC from Yahoo Finance (via existing /api/yahoo proxy) ────────────
const fetchOHLC = async (yfSymbol, range = '1y', interval = '1d') => {
  const yfRange = range === '9mo' ? '1y' : range;
  const url = `/api/yahoo/v8/finance/chart/${encodeURIComponent(yfSymbol)}?interval=${interval}&range=${yfRange}&includePrePost=false`;
  const res = await fetch(url);
  if (res.status === 404) throw new Error('NOT_FOUND');
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  const result = json?.chart?.result?.[0];
  if (!result) throw new Error('No data');

  const { timestamp, indicators } = result;
  const quote = indicators.quote[0];

  let data = timestamp
    .map((t, i) => ({
      time: t,                   // unix seconds
      open: quote.open[i],
      high: quote.high[i],
      low: quote.low[i],
      close: quote.close[i],
    }))
    .filter(d => d.open && d.high && d.low && d.close);

  if (range === '9mo') {
    const nineMonthsAgo = Math.floor(Date.now() / 1000) - (274 * 24 * 60 * 60);
    data = data.filter(d => d.time >= nineMonthsAgo);
  }

  return data;
};

// ─── Lightweight-charts candlestick component ─────────────────────────────────
const RANGES = ['1mo', '3mo', '6mo', '9mo', '1y', '2y', '5y'];

const LWChart = ({ name, yfSymbol, color, range: propRange, setRange: propSetRange }) => {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const [localRange, setLocalRange] = useState('1y');
  const range = propRange || localRange;
  const setRange = propSetRange || setLocalRange;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dataSource, setDataSource] = useState(null); // 'db' | 'yahoo' | null
  const [legendData, setLegendData] = useState(null);

  // Init chart once — skip entirely if yfSymbol is null (unsupported index)
  useEffect(() => {
    if (!containerRef.current || yfSymbol === null) return;
    const chart = createChart(containerRef.current, {
      width: containerRef.current.offsetWidth,
      height: containerRef.current.offsetHeight,
      layout: { background: { color: '#09090b' }, textColor: '#9ca3af' },
      grid: { vertLines: { color: '#1c1c1e' }, horzLines: { color: '#1c1c1e' } },
      crosshair: { mode: 1 },
      rightPriceScale: { borderColor: '#27272a' },
      timeScale: { borderColor: '#27272a', timeVisible: true },
      handleScroll: true,
      handleScale: true,
    });
    const series = chart.addCandlestickSeries({
      upColor: '#10b981', downColor: '#f43f5e',
      borderUpColor: '#10b981', borderDownColor: '#f43f5e',
      wickUpColor: '#10b981', wickDownColor: '#f43f5e',
    });
    // Price line matching the series color
    series.applyOptions({ priceLineColor: color });

    chartRef.current = chart;
    seriesRef.current = series;

    const ro = new ResizeObserver(() => {
      if (containerRef.current && chartRef.current) {
        chartRef.current.resize(
          containerRef.current.offsetWidth,
          containerRef.current.offsetHeight
        );
      }
    });
    ro.observe(containerRef.current);

    const handleCrosshairMove = (param) => {
      if (!param.time || param.point.x < 0 || param.point.x > containerRef.current.clientWidth || param.point.y < 0 || param.point.y > containerRef.current.clientHeight) {
        setLegendData(null);
        return;
      }
      
      const data = param.seriesPrices.get(seriesRef.current);
      if (data) {
        setLegendData({
          time: param.time,
          open: data.open,
          high: data.high,
          low: data.low,
          close: data.close,
        });
      } else {
        setLegendData(null);
      }
    };
    chart.subscribeCrosshairMove(handleCrosshairMove);

    return () => {
      ro.disconnect();
      chart.unsubscribeCrosshairMove(handleCrosshairMove);
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load data when name/yfSymbol or range changes — try DB first, fall back to Yahoo
  const loadData = useCallback(async () => {
    if (!seriesRef.current) return;
    setLoading(true);
    setError(null);
    try {
      // 1️⃣  Try backend DB (rsc_index_prices) — works for all Indian & synced indices
      if (name) {
        try {
          const data = await fetchOHLCFromDB(name, range);
          seriesRef.current.setData(data);
          chartRef.current?.timeScale().fitContent();
          setDataSource('db');
          return;
        } catch (dbErr) {
          if (!dbErr.message.includes('DB_NOT_FOUND')) {
            console.warn(`[LWChart] DB fetch failed for "${name}": ${dbErr.message}`);
          }
          // fall through to Stock DB try
        }

        // 1.5️⃣  Try backend DB (rsc_stock_prices) — for constituents/stocks
        try {
          const data = await fetchStockOHLCFromDB(name, range);
          seriesRef.current.setData(data);
          chartRef.current?.timeScale().fitContent();
          setDataSource('db');
          return;
        } catch (dbErr) {
          if (!dbErr.message.includes('DB_NOT_FOUND')) {
            console.warn(`[LWChart] Stock DB fetch failed for "${name}": ${dbErr.message}`);
          }
        }
      }

      // 2️⃣  Fall back to Yahoo Finance
      if (!yfSymbol) throw new Error('NOT_FOUND');
      const data = await fetchOHLC(yfSymbol, range, '1d');
      seriesRef.current.setData(data);
      chartRef.current?.timeScale().fitContent();
      setDataSource('yahoo');
    } catch (e) {
      setError(e.message === 'NOT_FOUND' ? 'NOT_FOUND' : (e.message || 'Failed to load data'));
    } finally {
      setLoading(false);
    }
  }, [name, yfSymbol, range]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Early return AFTER all hooks ──────────────────────────────────────────────
  // (Rules of Hooks: hooks must be called unconditionally, return can come after)
  if (yfSymbol === null) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2">
        <AlertCircle className="w-5 h-5 text-zinc-600" />
        <span className="text-[13px] text-zinc-500 font-bold">Chart unavailable</span>
        <span className="text-[12px] text-zinc-600 text-center px-4">
          Yahoo Finance does not carry chart data for this index
        </span>
      </div>
    );
  }



  return (
    <div className="flex flex-col h-full">
      {/* Range selector */}
      <div className="flex items-center gap-1 px-3 py-1.5 border-b border-zinc-800/60">
        {RANGES.map(r => (
          <button
            key={r}
            onClick={() => setRange(r)}
            className={`px-2.5 py-0.5 text-[12px] font-black uppercase rounded transition-all ${range === r
              ? 'bg-emerald-500 text-white'
              : 'text-zinc-500 hover:text-white hover:bg-zinc-800'
              }`}
          >
            {r.replace('mo', 'M').replace('y', 'Y')}
          </button>
        ))}
        {loading && <Loader2 className="w-3 h-3 text-emerald-500 animate-spin ml-2" />}
        {dataSource && !loading && (
          <span className={`ml-auto text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${
            dataSource === 'db'
              ? 'text-emerald-500 border-emerald-500/30 bg-emerald-500/5'
              : 'text-zinc-500 border-zinc-700 bg-zinc-900'
          }`}>
            {dataSource === 'db' ? 'DB ✓' : 'Yahoo'}
          </span>
        )}
      </div>

      {/* Chart */}
      <div className="relative flex-1 min-h-0">
        <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
        
        {/* OHLC Legend Overlay */}
        {legendData && (
          <div className="absolute top-2 left-2 z-10 flex items-center gap-3 px-2 py-1 bg-zinc-900/80 border border-zinc-800 rounded shadow-lg pointer-events-none backdrop-blur-sm">
            <div className="flex gap-1 items-center">
              <span className="text-[11px] font-bold text-zinc-500">O</span>
              <span className="text-[12px] font-mono text-zinc-300">{legendData.open?.toFixed(2)}</span>
            </div>
            <div className="flex gap-1 items-center">
              <span className="text-[11px] font-bold text-zinc-500">H</span>
              <span className="text-[12px] font-mono text-emerald-400">{legendData.high?.toFixed(2)}</span>
            </div>
            <div className="flex gap-1 items-center">
              <span className="text-[11px] font-bold text-zinc-500">L</span>
              <span className="text-[12px] font-mono text-rose-400">{legendData.low?.toFixed(2)}</span>
            </div>
            <div className="flex gap-1 items-center">
              <span className="text-[11px] font-bold text-zinc-500">C</span>
              <span className={`text-[12px] font-mono font-bold ${legendData.close >= legendData.open ? 'text-emerald-400' : 'text-rose-400'}`}>
                {legendData.close?.toFixed(2)}
              </span>
            </div>
          </div>
        )}

        {error && (
          error === 'NOT_FOUND' ? (
            // Symbol not carried by Yahoo Finance — no retry needed
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-zinc-950/80">
              <AlertCircle className="w-5 h-5 text-zinc-500" />
              <span className="text-[13px] text-zinc-400 font-bold">Chart unavailable</span>
              <span className="text-[12px] text-zinc-600 text-center px-4">
                Yahoo Finance does not carry chart data for <span className="text-zinc-400 font-mono">{yfSymbol}</span>
              </span>
            </div>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-zinc-950/80">
              <AlertCircle className="w-6 h-6 text-rose-400" />
              <span className="text-[13px] text-rose-400 font-bold">Could not load data</span>
              <span className="text-[12px] text-zinc-600">{error}</span>
              <button
                onClick={loadData}
                className="mt-1 px-3 py-1 text-[12px] font-black uppercase bg-zinc-800 hover:bg-zinc-700 text-white rounded transition-all"
              >
                Retry
              </button>
            </div>
          )
        )}
      </div>
    </div>
  );
};

// ─── PinnedChart card ─────────────────────────────────────────────────────────
const PinnedChart = ({ name, color, onUnpin, range, setRange }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const yfSymbol = resolveYFSymbol(name); // may be null for unsupported indices

  return (
    <div
      className="flex flex-col rounded-xl border overflow-hidden shadow-xl"
      style={{ borderColor: `${color}40`, background: '#09090b', boxShadow: `0 0 24px ${color}18` }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-3 py-2 border-b shrink-0"
        style={{ borderColor: `${color}30`, background: `${color}0a` }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-2 h-2 rounded-full shrink-0 animate-pulse" style={{ backgroundColor: color }} />
          <span className="font-black text-white text-[14px] uppercase tracking-tight truncate">{name}</span>
          <span className="text-[11px] font-bold text-zinc-500 bg-zinc-900/80 px-2 py-0.5 rounded border border-zinc-800 font-mono shrink-0">
            {yfSymbol}
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <a
            href={`https://finance.yahoo.com/chart/${encodeURIComponent(yfSymbol)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1 rounded hover:bg-white/10 text-zinc-500 hover:text-white transition-colors"
            title="Open in Yahoo Finance"
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
          <button
            onClick={() => setIsExpanded(v => !v)}
            className="p-1 rounded hover:bg-white/10 text-zinc-500 hover:text-white transition-colors"
          >
            {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
          <button
            onClick={() => onUnpin(name)}
            className="p-1 rounded hover:bg-rose-500/20 text-zinc-500 hover:text-rose-400 transition-colors"
            title="Unpin"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Chart body */}
      {isExpanded && (
        <div style={{ height: 720 }}>
          <LWChart name={name} yfSymbol={yfSymbol} color={color} range={range} setRange={setRange} />
        </div>
      )}
    </div>
  );
};

// ─── Panel ────────────────────────────────────────────────────────────────────
export const TradingViewChartsPanel = ({ pinnedSymbols, colorMap, onUnpin, onUnpinAll }) => {
  const [globalRange, setGlobalRange] = useState(() => localStorage.getItem('rs_tv_range') || '1y');

  const handleRangeChange = useCallback((r) => {
    setGlobalRange(r);
    localStorage.setItem('rs_tv_range', r);
  }, []);

  if (!pinnedSymbols?.length) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-3">
          <TrendingUp className="w-4 h-4 text-emerald-500" />
          <span className="text-[15px] font-black text-white uppercase tracking-tighter">Pinned Charts</span>
          <div className="flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-0.5 rounded-full">
            <Pin className="w-2.5 h-2.5 text-emerald-400 fill-emerald-400/40" />
            <span className="text-[12px] font-black text-emerald-400">{pinnedSymbols.length} pinned</span>
          </div>
          <span className="text-[12px] text-zinc-600 font-bold uppercase tracking-widest">Yahoo Finance · NSE/BSE Live</span>
        </div>
        <button
          onClick={onUnpinAll}
          className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-black uppercase tracking-widest bg-zinc-900 hover:bg-rose-500/10 text-zinc-500 hover:text-rose-400 border border-zinc-800 hover:border-rose-500/30 rounded-lg transition-all"
        >
          <X className="w-3 h-3" />
          Clear All
        </button>
      </div>

      <div className="grid gap-4 grid-cols-1">
        {pinnedSymbols.length > 0 && (() => {
          const latestName = pinnedSymbols[pinnedSymbols.length - 1];
          return (
            <PinnedChart
              key={latestName}
              name={latestName}
              color={colorMap[latestName] || '#10b981'}
              onUnpin={onUnpin}
              range={globalRange}
              setRange={handleRangeChange}
            />
          );
        })()}
      </div>
    </div>
  );
};

export default PinnedChart;
